import {
  HVButton,
  HVIcon,
  HVScrollBar,
  HVText,
  HVTooltip
} from "@repo/HVComponents";
import { useEffect, useRef, useState } from "react";
import { useLang } from "@hooks/useLang";
import _ from "lodash";
import ModalManager from "@utils/ModalManager";
import useDetectOutsideClick from "@hooks/useDetectOutsideClick";
import { Input } from "@components/common/input";
import { useInputHook } from "@hooks/useInputHook";
import { MAX_LENGTH_ANALYTICS_NAME } from "@resources/Constants";
import { ICON } from "src/constants/icons";
import * as S from "./FilterListAccordionButton.styled";

const DEBOUNCE_TIME_MS = 100;
export type MORE_TYPE = "EDIT_FILTER_NAME" | "REMOVE_FILTER";

interface AccordionProps {
  disableMore: boolean;
  defaultId?: string;
  list: {
    value: string;
    label: string;
  }[];
  onChangeFilter: (id: string) => void;
  onChangeMoreType: (status: MORE_TYPE, id: string) => void;
  onChangeFiltereName: (name: string, id: string) => void;
}
export const FilterListAccordionButton = (props: AccordionProps) => {
  const [isShow, setIsShow] = useState(false);
  const listRef = useRef<HTMLDivElement>(null); // S.List에 대한 참조 생성
  const [moreTop, setMoreTop] = useState<undefined | number>(undefined);
  const { translate } = useLang();
  const rootRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const inputWrapRef = useRef<HTMLDivElement>(null);

  const { inputRef, getValueFromRef, setValue, value } = useInputHook<string>(
    props.list[0].label
  );
  const menuOpenIdx = useRef<number>(-1);

  useEffect(() => {
    if (isEditMode) {
      inputRef.current?.focus();
    }
  }, [isEditMode]);

  const handleClickSelectedButton = () => {
    const next = !isShow;
    if (next && listRef.current) {
      listRef.current.scrollTop = 0;
    }
    setIsShow(next);
  };

  const handleClickMore = (
    e: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setIsEditMode(false);
    e.stopPropagation();
    menuOpenIdx.current = index;
    const bh = 40; // more button height
    const ih = 28; // item height in more button height

    setMoreTop(
      e.currentTarget.offsetTop - (listRef.current?.scrollTop ?? 0) + bh + ih
    );
  };

  const handleClickItem = _.debounce(
    (e: React.MouseEvent<HTMLUListElement>) => {
      e.stopPropagation();
      const status = (e.target as HTMLElement).dataset.id as MORE_TYPE;
      const id = props.list[menuOpenIdx.current].value;

      if (status === "REMOVE_FILTER") {
        ModalManager.confirm(translate("analytics_remove_desc"), {
          title: translate("analytics_remove_title"),
          labelConfirm: translate("ok"),
          labelCancel: translate("cancel"),
          onSubmit: () => {
            setMoreTop(undefined);
            props.onChangeMoreType(status, id);
          }
        });
      } else {
        setMoreTop(undefined);
        setValue(props.list[menuOpenIdx.current].label);
        setIsEditMode(true);

        // props.onChangeMoreType(status);
      }
    },
    DEBOUNCE_TIME_MS
  );

  const updateName = _.debounce(() => {
    const { label: origin, value: id } = props.list[menuOpenIdx.current];
    const changed = getValueFromRef();
    if (changed && origin !== changed) {
      props.onChangeFiltereName(changed, id);
    }

    if (!changed) {
      setValue(origin);
    }

    _.defer(() => {
      setIsEditMode(false);
    });
  }, DEBOUNCE_TIME_MS);

  useDetectOutsideClick(inputWrapRef, () => {
    updateName();
  });

  useDetectOutsideClick(rootRef, () => {
    setMoreTop(undefined);
  });

  const handleChangeScroll = _.throttle(() => {
    setMoreTop(undefined);
  }, 100);

  const selected = (() => {
    const v = props.list.find((item) => item.value === props.defaultId);
    if (v) {
      return v;
    }

    return props.list[0];
  })();
  if (!selected) {
    return null;
  }
  const isShowDropDown = !!moreTop;

  return (
    <S.Container isShow={isShow} style={{ position: "relative" }}>
      <S.SelectedButton
        onClick={handleClickSelectedButton}
        data-testid="selected-button"
      >
        <HVIcon
          name="filter"
          svgProps={{ ...ICON.SIZE_20, ...ICON.COLOR_STROKE }}
        />

        <span>{selected?.label}</span>
        <HVIcon
          name="arrow_chevron_down"
          svgProps={{ ...ICON.SIZE_20, ...ICON.COLOR_STROKE }}
          styleClass="accordion-arrow-icon"
        />
      </S.SelectedButton>

      <S.AccordionList>
        <HVScrollBar
          scrollBarProps={{
            h: "100px",
            scrollbars: "y",
            type: "auto",
            p: 0,
            pr: 1,
            viewportRef: listRef,
            onScrollPositionChange: handleChangeScroll
          }}
        >
          {props.list.map((item, idx) => {
            const classList = [];
            if (item.value === selected?.value) {
              classList.push("selected");
            }

            return (
              <S.AccordionListItem
                // eslint-disable-next-line react/no-array-index-key
                key={`accordion-item-${idx}`}
                className={classList.join(" ")}
                onClick={() => {
                  if (props.defaultId !== item.value) {
                    setIsEditMode(false);
                    props.onChangeFilter(item.value);
                  }
                }}
              >
                {(() => {
                  // edit mode
                  if (isEditMode && idx === menuOpenIdx.current) {
                    return (
                      <div
                        ref={inputWrapRef}
                        style={{
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            updateName();
                          }
                        }}
                      >
                        <Input
                          innerRef={inputRef}
                          value={value}
                          onChange={(e) => setValue(e.target.value)}
                          style={{
                            width: "228px",
                            height: "24px",
                            paddingRight: 35
                          }}
                          maxLength={MAX_LENGTH_ANALYTICS_NAME}
                        />
                        <HVButton
                          variant="Ghost"
                          buttonContainerProps={{
                            w: 20,
                            h: 20,
                            p: 0,
                            styles: {
                              root: {
                                position: "absolute",
                                right: 8,
                                display: "flex"
                              }
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setValue("");
                            inputRef.current?.focus();
                          }}
                        >
                          <HVIcon
                            name="fail"
                            scale="large"
                            theme="fill"
                            svgProps={{
                              ...ICON.SIZE_20,
                              stroke:
                                "var(--hv-text-on-brand-tertiary) !important"
                            }}
                          />
                        </HVButton>
                      </div>
                    );
                  }

                  // list item
                  return (
                    <>
                      <span style={{ paddingLeft: "8px" }}>{item.label}</span>
                      {!props.disableMore && (
                        <S.TooltipWrapper>
                          <HVTooltip
                            label="" // use tooltipProps.label
                            position="top"
                            tooltipProps={{
                              children: undefined,
                              label: (
                                <HVText
                                  type="Body-xs"
                                  c="var(--hv-text-on-brand-primary)"
                                >
                                  {translate("more")}
                                </HVText>
                              ),
                              disabled: isShowDropDown
                            }}
                          >
                            <button
                              onClick={(e) => handleClickMore(e, idx)}
                              data-testid="more-icon-selected"
                            >
                              <HVIcon
                                name="more"
                                svgProps={{ width: 24, height: 24 }}
                                styleClass="more-icon"
                              />
                            </button>
                          </HVTooltip>
                        </S.TooltipWrapper>
                      )}
                    </>
                  );
                })()}
              </S.AccordionListItem>
            );
          })}
        </HVScrollBar>
      </S.AccordionList>
      {isShowDropDown && (
        <S.DropDownList
          ref={rootRef}
          onClick={handleClickItem}
          style={{ top: `${moreTop}px` }}
        >
          <li data-id={"EDIT_FILTER_NAME" as const}>
            {translate("edit_name")}
          </li>
          <li data-id={"REMOVE_FILTER" as const}>{translate("remove")}</li>
        </S.DropDownList>
      )}
    </S.Container>
  );
};
